import {
  CalendarDaysIcon,
  MoreHorizontalIcon,
  PencilLineIcon,
  PlayIcon,
  Trash2Icon
} from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { VideoNoticeVideoNoticeCollectionReadModel } from '@r/api/generated/client.schemas.ts';
import { Badge } from '@ui/badge.tsx';
import { Button } from '@ui/button.tsx';
import { Card } from '@ui/card.tsx';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@ui/dropdown-menu.tsx';

interface VideoNoticeCardProps {
  videoNotice: VideoNoticeVideoNoticeCollectionReadModel;
  onEdit?: (videoNotice: VideoNoticeVideoNoticeCollectionReadModel) => void;
  onDelete?: (videoNotice: VideoNoticeVideoNoticeCollectionReadModel) => void;
  onOpen?: (videoNotice: VideoNoticeVideoNoticeCollectionReadModel) => void;
}

/**
 * Présente une notice sous forme de carte avec un aperçu vidéo non interactif.
 * Selon les callbacks fournis, la carte ouvre le lecteur ou affiche les actions de gestion.
 */
export function VideoNoticeCard({ videoNotice, onEdit, onDelete, onOpen }: VideoNoticeCardProps) {
  const { t, i18n } = useTranslation();
  const formattedDate = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language, {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      }).format(new Date(videoNotice.createdAt)),
    [i18n.language, videoNotice.createdAt]
  );

  return (
    <Card
      className={`overflow-hidden rounded-xl ${onOpen ? 'cursor-pointer transition-shadow hover:shadow-md' : ''}`}
      role={onOpen ? 'button' : undefined}
      tabIndex={onOpen ? 0 : undefined}
      onClick={() => onOpen?.(videoNotice)}
      onKeyDown={(event) => {
        if (onOpen && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault();
          onOpen(videoNotice);
        }
      }}
    >
      <div className="overflow-hidden relative aspect-video bg-muted">
        <video
          src={videoNotice.videoUrl}
          muted
          playsInline
          preload="metadata"
          aria-hidden="true"
          tabIndex={-1}
          className="object-cover pointer-events-none size-full"
          onLoadedMetadata={(event) => {
            event.currentTarget.currentTime = Math.min(0.1, event.currentTarget.duration);
          }}
        />
        <div className="flex absolute inset-0 justify-center items-center pointer-events-none bg-black/10">
          <span className="flex justify-center items-center rounded-full shadow size-11 bg-white/90 text-foreground">
            <PlayIcon className="ml-0.5 size-5 fill-current" />
          </span>
        </div>
      </div>

      <div className="p-4 space-y-3">
        <div className="flex gap-2 justify-between items-start">
          <h3 className="font-semibold leading-snug line-clamp-2">{videoNotice.title}</h3>

          {onEdit && onDelete && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="-mt-2 -mr-2 size-8 shrink-0"
                  aria-label={t('fiddy.admin.video.notices.actions.label', {
                    title: videoNotice.title
                  })}
                >
                  <MoreHorizontalIcon className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onEdit(videoNotice)}>
                  <PencilLineIcon className="mr-2 size-4" />
                  {t('fiddy.admin.video.notices.actions.edit')}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={() => onDelete(videoNotice)}
                >
                  <Trash2Icon className="mr-2 size-4" />
                  {t('fiddy.admin.video.notices.actions.delete')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {videoNotice.description && (
          <p className="line-clamp-3 text-sm leading-relaxed text-muted-foreground">
            {videoNotice.description}
          </p>
        )}

        <div className="flex flex-wrap gap-2 justify-between items-center text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <CalendarDaysIcon className="size-3.5" />
            {formattedDate}
          </span>
          {videoNotice.category && (
            <Badge variant="secondary" className="font-normal">
              {videoNotice.category}
            </Badge>
          )}
        </div>
      </div>
    </Card>
  );
}
